import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  type ForwardedRef
} from "react";
import { createPortal, render } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Close } from "@certa/icons/components/Close";

// Move these icons to @certa/icons once the new icons are finalised
import { Info } from "../../icons/Info";
import { Error } from "../../icons/Error";
import { Warning } from "../../icons/Warning";
import { Success } from "../../icons/Success";
import loadingIcon from "../../icons/loading.svg";
import { nanoid } from "nanoid";
import styles from "./Toast.module.css";

import { classNames } from "../../utils/common";
import { CATALYST_Z_INDEXES } from "../../constants/styles";

export enum ToastTypes {
  LOADING = "Loading",
  SUCCESS = "Success",
  INFO = "Info",
  ERROR = "Error",
  WARNING = "Warning"
}

export enum ToastPlacements {
  BOTTOM_RIGHT = "BottomRight",
  BOTTOM_LEFT = "BottomLeft"
}

export type ToastProps = {
  id?: string;
  /**
   * @deprecated - Not recommended for general use.
   * Currently maintained for legacy support, but will likely be replaced.
   * We plan to implement a better pattern for toast management and custom dismissal.
   * For now, rely on the auto-generated nanoid for toast identification.
   *
   * Future updates may deprecate this in favor of a more robust solution.
   */
  toastKey?: string;
  show: boolean;
  placement?: ToastPlacements;
  type?: ToastTypes;
  /**
   *  Defaults to true, so toasts auto dismiss after set duration or default duration of 5s.
   *
   *  If set to false, the toast will need to be dismissed on manual trigger.
   * */
  shouldAutoDismiss?: boolean;
  duration?: number;
  title: string;
  /**
   * Description can be a string or ReactNode.
   */
  description?: string | React.ReactNode;
  onClose?: () => void;
  /**
   * Optional action button configuration.
   * Common uses:
   * - "Retry"
   * - "View Details"
   * - "Undo"
   */
  action?: {
    onClick: () => void;
    label: string;
  };
};

const activeToasts = new Map<
  string,
  {
    ref: React.RefObject<{ close: () => void }>;
  }
>();

const ToastAction = ({ action }: { action?: ToastProps["action"] }) => {
  if (!action) return null;

  return (
    <button
      type="button"
      onClick={action.onClick}
      className={styles.catalystToastActionButton}
      aria-label={action.label}
    >
      {action.label}
    </button>
  );
};

export const Toast = React.forwardRef(
  (props: ToastProps, ref: ForwardedRef<{ close: () => void }>) => {
    const {
      id,
      show: shouldShow,
      placement = ToastPlacements.BOTTOM_RIGHT,
      type = ToastTypes.INFO,
      title,
      description,
      shouldAutoDismiss = true,
      duration = 5000,
      onClose,
      action
    } = props;
    const rootElement = document.getElementById("catalyst-toasts-wrapper");

    const [shouldShowToast, setShouldShowToast] = useState<boolean>(false);

    useEffect(() => {
      if (rootElement) {
        rootElement.setAttribute("role", "status");
        rootElement.className = styles["catalystToast" + placement];
        rootElement.style.zIndex = CATALYST_Z_INDEXES.TOAST;
      }
      setShouldShowToast(shouldShow);
    }, [placement, rootElement, shouldShow]);

    const timerRef = useRef<NodeJS.Timeout | undefined>();

    useEffect(() => {
      if (shouldAutoDismiss) {
        timerRef.current = setTimeout(() => {
          setShouldShowToast(false);
          onClose && onClose();
          if (id) activeToasts.delete(id);
        }, duration);

        return () => {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }
          if (id) activeToasts.delete(id);
        };
      }
    }, [shouldAutoDismiss, duration, onClose, id]);

    const getToastTypeIcon = () => {
      switch (true) {
        case type === ToastTypes.SUCCESS:
          return <Success />;
        case type === ToastTypes.WARNING:
          return <Warning />;
        case type === ToastTypes.ERROR:
          return <Error />;
        case type === ToastTypes.INFO:
          return <Info />;
      }
    };

    const handleClose = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setShouldShowToast(false);
      onClose && onClose();
      if (id) activeToasts.delete(id);
    };

    // Expose the handleClose method via a ref
    useImperativeHandle(ref, () => ({
      close: handleClose
    }));

    return rootElement && shouldShowToast
      ? createPortal(
          <AnimatePresence>
            <motion.div
              className={classNames({
                [styles.catalystToast]: true,
                [styles["catalystToast" + type]]: true
              })}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              {type === ToastTypes.LOADING ? (
                <>
                  <div className={styles.catalystToastLoadingIcon}>
                    <img src={loadingIcon} alt={`toast ${type} icon`} />
                  </div>
                  <div className={styles.catalystToastTitle}>{title}</div>
                </>
              ) : (
                <>
                  <div className={styles.catalystToastTypeIcon}>
                    {getToastTypeIcon()}
                  </div>
                  <div className={styles.catalystToastMessage}>
                    <div className={styles.catalystToastTitle}>{title}</div>
                    {description && (
                      <div className={styles.catalystToastDescription}>
                        {description}
                      </div>
                    )}
                    <ToastAction action={action} />
                  </div>
                  <div
                    className={styles.catalystToastClose}
                    onClick={handleClose}
                  >
                    <Close size={12} />
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>,
          rootElement
        )
      : null;
  }
);

/**
 * Shows a one-time toast notification. Uses DocumentFragment for better performance
 * as these toasts are transient and don't need to be referenced or updated later.
 * DocumentFragment is ideal here because:
 * 1. It's a lightweight container that doesn't create actual DOM nodes
 * 2. Perfect for one-time insertions that don't need to be tracked
 * 3. Reduces DOM reflows when adding multiple elements
 */
export function showToast(params: Omit<ToastProps, "show">): string {
  const { toastKey, ...restParams } = params;
  const id = toastKey ? toastKey : nanoid();
  const toastRef = React.createRef();

  if (toastKey && activeToasts.has(toastKey)) {
    return toastKey;
  }

  const toastContainer = document.createDocumentFragment();
  render(
    <Toast
      {...restParams}
      id={id}
      ref={toastRef as React.RefObject<{ close: () => void }>}
      show={true}
    />,
    toastContainer
  );
  activeToasts.set(id, {
    ref: toastRef as React.RefObject<{ close: () => void }>
  });
  return id;
}

export function dismissToast(id: string): void {
  const toastData = activeToasts.get(id);
  if (toastData) {
    const { ref } = toastData;
    if (ref.current && ref.current.close) {
      ref.current.close();
    }
  }
}
